/* --------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

import _ from 'lodash';
import moment from 'moment';
import service from '../../services/auth';

const AUTH_SIGNIN = 'AUTH_SIGNIN';
const AUTH_SWITCH_ORG = 'AUTH_SWITCH_ORG';
const AUTH_SIGNOUT = 'AUTH_SIGNOUT';
const AUTH_LOAD_INVITE = 'AUTH_LOAD_INVITE';
const AUTH_SWITCH_SIGNIN_LINK = 'AUTH_SWITCH_SIGNIN_LINK';

const state = {
  key: null,
  keyExpires: null,
  orgCode: '',
  isAuthenticated: false,
  invite: null, // for sign up page
  signInLink: { name: 'sign-in' }
};

const mutations = {
  [AUTH_SIGNIN] (state, authData) {
    state.key = authData.key;
    state.keyExpires = authData.expires;
    state.isAuthenticated = true;
  },

  [AUTH_SWITCH_ORG] (state, orgCode) {
    state.orgCode = orgCode;
  },

  [AUTH_SWITCH_SIGNIN_LINK] (state, signInLink) {
    state.signInLink = signInLink;
  },

  [AUTH_SIGNOUT] (state) {
    state.key = null;
    state.keyExpires = null;
    state.isAuthenticated = false;
  },

  [AUTH_LOAD_INVITE] (state, invite) {
    state.invite = invite;
  }
};

const actions = {
  signIn: ({ dispatch }, data) => {
    return handleSignInPromise(service.signIn(data), dispatch);
  },
  signUp: ({ dispatch, commit }, data) => {
    // Clone the data then remove "privacyAccepted"
    const _data = _.cloneDeep(data);
    if (_data.privacyAccepted === false) {
      delete _data.privacyAccepted;
    }

    return handleSignInPromise(service.signUp(_data), dispatch)
      .then(() => {
        commit(AUTH_LOAD_INVITE, null);
      });
  },
  signInFromLocalStorage: ({ commit, dispatch }, newAuthData) => {
    if (newAuthData) {
      setFromLocalStorage('key', newAuthData.key);
      setFromLocalStorage('keyexp', newAuthData.keyExpires);
      setFromLocalStorage('org', newAuthData.orgCode);
    }
    const storedKey = getSanitizedFromLocalStorage('key');
    const storedOrgCode = getSanitizedFromLocalStorage('org');
    if (storedKey && storedOrgCode) {
      const storedKeyExpires = getFromLocalStorage('keyexp');
      if (new Date() < new Date(storedKeyExpires)) {
        commit(AUTH_SIGNIN, {key: storedKey, expires: storedKeyExpires});
        return window.Promise.all([
          dispatch('switchOrg', storedOrgCode),
          dispatch('getUser'),
        ]);
      }
      return true;
    } else if (newAuthData) {
      return dispatch('notify', {
        type: 'error',
        subject: 'Unable to Log In',
        text: `You appear to have cookies disabled in your browser.
              VoiceVibes requires the ability to set cookies to store your
              login information securely. Please reenable cookies in your
              browser and try logging in again.`
      });
    }
    return false;
  },
  switchOrg: ({ commit, dispatch }, orgCode) => {
    setFromLocalStorage('org', orgCode);
    commit(AUTH_SWITCH_ORG, orgCode);
    dispatch('resetData');
    if (orgCode) {
      dispatch('getOrg');
      dispatch('listGroups');
    }
  },
  resetSignInLink: ({ commit }) => {
    commit(AUTH_SWITCH_SIGNIN_LINK, { name: 'sign-in' });
  },
  switchSignInLink: ({ commit }, signInLink) => {
    commit(AUTH_SWITCH_SIGNIN_LINK, signInLink);
  },
  autoSignIn: ({ dispatch }, signInKey) => {
    if (!signInKey) {
      throw new Error('No sign in key provided');
    }
    const keyParts = signInKey.split('-');
    return dispatch('signInFromLocalStorage', {
      orgCode: keyParts[0],
      key: keyParts[1],
      keyExpires: moment().add(1, 'hour').toDate()
    });
  },
  resetData: ({ dispatch }) => {
    dispatch('resetGroups');
    dispatch('resetRecordings');
  },
  signOut: ({ commit, dispatch }) => {
    service.signOut().catch(() => {}); // silencing errors
    removeFromLocalStorage('key');
    removeFromLocalStorage('keyexp');
    commit(AUTH_SIGNOUT);
    dispatch('switchOrg', '');
    dispatch('resetUser');
  },
  getInvite: ({ commit }, data) => {
    return service.getInvite(_.pick(data, ['inviteId', 'inviteCode']))
      .then(invite => {
        commit(AUTH_LOAD_INVITE, invite);
        return invite;
      });
  }
};

const getters = {
  authApiKey: state => state.key,
  authOrgCode: state => state.orgCode,
  isAuthenticated: state => state.isAuthenticated,
  signUpInvite: state => state.invite,
  signInLink: state => state.signInLink
};

function handleSignInPromise(promise, dispatch) {
  return promise.then(authData => {
    return dispatch('signInFromLocalStorage', {
      key: authData.auth.apiKey,
      keyExpires: authData.auth.expires,
      orgCode: authData.orgs[0].apiCode
    });
  });
}

function getFromLocalStorage(key) {
  try {
    return localStorage.getItem(key) || '';
  } catch(err) {
    return '';
  }
}

function setFromLocalStorage(key, val) {
  try {
    localStorage.setItem(key, val);
  } catch(err) {
    return;
  }
}

function removeFromLocalStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch(err) {
    return;
  }
}

function getSanitizedFromLocalStorage(key) {
  return (getFromLocalStorage(key) || '').replace(/[^a-z0-9.]/ig, '');
}

export default {
  state,
  mutations,
  actions,
  getters
};
